import { Button, Modal, Select, TextInput, Textarea } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchClientTypes,
  fetchMarketSegments,
  fetchMarketSubSegments,
  fetchStates,
} from '../../Actions/CommonFields';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { Client } from '../../Models/Client';
import ClientApi from '../../Api/ClientApi';
import { UserLevelNames } from '../../Models/UserLevel';

export default function ClientAddModal({
  show,
  setShow,
  getClients,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  getClients: () => Promise<void> | undefined;
}) {
  const dispatch = useAppDispatch();
  const { clientTypes, marketSegments, marketSubSegments, states } = useAppSelector(
    (state) => state.commonFields
  );
  const { userModel } = useAppSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [marketSegmentId, setMarketSegmentId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Client>({
    id: 0,
    name: null,
    clientTypeId: null,
    marketSubSegmentId: null,
    address: null,
    city: null,
    stateId: null,
    zip: null,
    phone: null,
    email: null,
    description: null,
    website: null,
    accountNumber: null,
    quickbooksId: null,
    dateCreated: null,
    countryId: null,

    clientLocations: [],
    clientType: null,
    country: null,
    marketSubSegment: null,
    state: null,
  });

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      name: null,
      clientTypeId: null,
      marketSubSegmentId: null,
      address: null,
      city: null,
      stateId: null,
      zip: null,
      phone: null,
      email: null,
      description: null,
      website: null,
      accountNumber: null,
      quickbooksId: null,
      dateCreated: null,
      countryId: null,

      clientLocations: [],
      clientType: null,
      country: null,
      marketSubSegment: null,
      state: null,
    };
    setFormData(values as Client);
    setMarketSegmentId(null);
  };

  const getReferences = async () => {
    if (
      !clientTypes.length ||
      !marketSegments.length ||
      !marketSubSegments.length ||
      !states.length
    ) {
      await Promise.all([
        dispatch(fetchClientTypes()),
        dispatch(fetchMarketSegments()),
        dispatch(fetchMarketSubSegments()),
        dispatch(fetchStates()),
      ]);
    }
  };

  useEffect(() => {
    if (show === true) {
      getReferences();
    }
  }, [show]);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleMarketSegmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const segId = value ? Number(value) : null;
    setMarketSegmentId(segId);
    // reset subsegment if parent changes
    setFormData((prevData) => ({ ...prevData, marketSubSegmentId: null }));
  };

  const handleMarketSubSegmentChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;
    const subId = value ? Number(value) : null;
    setFormData((prevData) => ({ ...prevData, marketSubSegmentId: subId }));

    // keep parent in sync if user selects a subsegment directly (or after load)
    if (subId) {
      const sub = marketSubSegments.find((x) => x.id === subId);
      if (sub?.marketSegmentId) {
        setMarketSegmentId(sub.marketSegmentId);
      }
    }
  };

  // When editing/resetting form state externally (or reopening), keep parent segment derived from selected subsegment.
  useEffect(() => {
    if (!formData.marketSubSegmentId) {
      return;
    }
    const sub = marketSubSegments.find((x) => x.id === formData.marketSubSegmentId);
    if (sub?.marketSegmentId) {
      setMarketSegmentId(sub.marketSegmentId);
    }
  }, [formData.marketSubSegmentId, marketSubSegments]);

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    setButtonDisabled(true);
    e.preventDefault();
    try {
      setIsLoading(true);
      if (!formData.clientTypeId) {
        formData.clientTypeId = null;
      }
      if (!formData.marketSubSegmentId) {
        formData.marketSubSegmentId = null;
      }
      return ClientApi.addUpdateClient(formData)
        .then((res) => {
          if (res.data.success) {
            toast.success('Success');
            setIsLoading(false);
            setButtonDisabled(false);
            getClients();
            onCloseModal();
          } else {
            toast.error(res.data.message);
            setIsLoading(false);
            setButtonDisabled(false);
          }
        })
        .catch((err) => {
          console.error(err);
          toast.error('Error: ' + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };
  function onCloseModal() {
    handleResetFormValues();
    setShow(false);
  }
  return (
    <Modal show={show} size='4xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='flex justify-center mb-2'>
            <h1 className='text-gray-900 font-bold text-4xl'>{`Add Client`}</h1>
          </div>
          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form onSubmit={onSubmit}>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Name:
                            </div>
                          </div>
                          <div className='flex'>
                            <div className='w-full'>
                              <TextInput
                                id='name'
                                name='name'
                                type='text'
                                placeholder='Name...'
                                onChange={handleChangeInput}
                                value={formData.name ?? ''}
                                sizing='md'
                              />
                            </div>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Account #:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='accountNumber'
                              name='accountNumber'
                              type='text'
                              placeholder='Account #...'
                              onChange={handleChangeInput}
                              value={formData.accountNumber ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Type:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='clientTypeId'
                              name='clientTypeId'
                              onChange={handleChangeSelect}
                              value={formData.clientTypeId ?? ''}
                              sizing='md'
                              disabled={
                                userModel?.userLevelId !== UserLevelNames.Admin
                              }
                            >
                              <option value={''}>(Select)</option>
                              {clientTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Market Segment:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='marketSegmentId'
                              name='marketSegmentId'
                              onChange={handleMarketSegmentChange}
                              value={marketSegmentId ?? ''}
                              sizing='md'
                              disabled={
                                userModel?.userLevelId !== UserLevelNames.Admin
                              }
                            >
                              <option value={''}>(Select)</option>
                              {marketSegments.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Market Sub-Segment:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='marketSubSegmentId'
                              name='marketSubSegmentId'
                              onChange={handleMarketSubSegmentChange}
                              value={formData.marketSubSegmentId ?? ''}
                              sizing='md'
                              disabled={
                                userModel?.userLevelId !== UserLevelNames.Admin ||
                                !marketSegmentId
                              }
                              helperText={!marketSegmentId ? 'Select Market Segment' : ''}
                            >
                              <option value={''}>(Select)</option>
                              {marketSubSegments
                                .filter(
                                  (x) => x.marketSegmentId === Number(marketSegmentId)
                                )
                                .map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.name}
                                  </option>
                                ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Email:
                            </div>
                          </div>
                          <div className='flex'>
                            <div className='w-full'>
                              <TextInput
                                id='email'
                                name='email'
                                type='text'
                                placeholder='Email...'
                                onChange={handleChangeInput}
                                value={formData.email ?? ''}
                                sizing='md'
                                required
                              />
                            </div>
                            {/* {emailExists && (
                          <div className='flex w-2/3 text-red-600'>
                            <div className='content-center px-2'>
                              <HiInformationCircle />
                            </div>
                            <div className='content-center'>
                              Email already exists
                            </div>
                          </div>
                        )} */}
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Phone:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='phone'
                              name='phone'
                              type='text'
                              placeholder='Phone...'
                              onChange={handleChangeInput}
                              value={formData.phone ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Address:
                            </div>
                          </div>
                          <div className='flex flex-col'>
                            <div className='w-full'>
                              <TextInput
                                id='address'
                                name='address'
                                type='text'
                                placeholder='Street...'
                                onChange={handleChangeInput}
                                value={formData.address ?? ''}
                                sizing='md'
                              />
                            </div>
                            <div className='w-full flex'>
                              <div className='w-1/3'>
                                <TextInput
                                  id='city'
                                  name='city'
                                  type='text'
                                  placeholder='City...'
                                  onChange={handleChangeInput}
                                  value={formData.city ?? ''}
                                  sizing='md'
                                />
                              </div>
                              <div className='w-1/3'>
                                <Select
                                  id='stateId'
                                  name='stateId'
                                  onChange={handleChangeSelect}
                                  value={formData.stateId ?? ''}
                                  sizing='md'
                                >
                                  <option>(State)</option>
                                  {states.map((state) => (
                                    <option key={state.id} value={state.id}>
                                      {state.name}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <div className='w-1/3'>
                                <TextInput
                                  id='zip'
                                  name='zip'
                                  type='text'
                                  placeholder='Zip...'
                                  onChange={handleChangeInput}
                                  value={formData.zip ?? ''}
                                  sizing='md'
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Website:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='website'
                              name='website'
                              type='text'
                              placeholder='Website...'
                              onChange={handleChangeInput}
                              value={formData.website ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Description:
                            </div>
                          </div>
                          <div>
                            <Textarea
                              id='description'
                              name='description'
                              placeholder='Description...'
                              onChange={handleChangeTextArea}
                              value={formData.description ?? ''}
                              rows={4}
                              autoComplete='new-password'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
